/**
 * WCAG contrast utilities (v146.52 · Feb 2026)
 * -----------------------------------------------
 * Reads the currently-rendered `.ah-root` node's CSS variables and
 * reports contrast ratios for the two most-viewed text-on-background
 * pairings: page-body-on-page-bg and block-text-on-block-bg.
 *
 * Uses the WCAG 2.1 relative luminance formula:
 *   L = 0.2126 * R + 0.7152 * G + 0.0722 * B
 *   where each channel is srgb→linear-corrected first.
 *
 * Contrast ratio = (L1 + 0.05) / (L2 + 0.05), with L1 = lighter.
 * Ratings (AA body text = 4.5, AA large text = 3, AAA body = 7).
 */

// ---------- colour parsing -------------------------------------------------

function _clamp01(v) { return Math.max(0, Math.min(1, v)); }

function _parseHex(hex) {
  const s = hex.replace(/^#/, "").trim();
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?([0-9a-fA-F]{2})?$/.test(s)) return null;
  let r, g, b, a = 1;
  if (s.length === 3) {
    r = parseInt(s[0] + s[0], 16);
    g = parseInt(s[1] + s[1], 16);
    b = parseInt(s[2] + s[2], 16);
  } else {
    r = parseInt(s.slice(0, 2), 16);
    g = parseInt(s.slice(2, 4), 16);
    b = parseInt(s.slice(4, 6), 16);
    if (s.length === 8) a = parseInt(s.slice(6, 8), 16) / 255;
  }
  return { r, g, b, a };
}

function _parseRgb(str) {
  const m = str.match(/^rgba?\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*(?:,\s*(-?\d+(?:\.\d+)?))?\s*\)$/);
  if (!m) return null;
  return {
    r: _clamp01(parseFloat(m[1]) / 255) * 255,
    g: _clamp01(parseFloat(m[2]) / 255) * 255,
    b: _clamp01(parseFloat(m[3]) / 255) * 255,
    a: m[4] !== undefined ? _clamp01(parseFloat(m[4])) : 1,
  };
}

/**
 * Parse a CSS colour string to `{r, g, b, a}` in 0-255 sRGB space.
 * Handles hex (#abc, #aabbcc, #aabbccdd), rgb()/rgba(). Anything more
 * exotic (hsl/lab/gradient) yields null — the caller should treat those
 * as "cannot assess" and hide the badge.
 */
export function parseColor(str) {
  if (!str) return null;
  const s = String(str).trim();
  if (s.startsWith("#")) return _parseHex(s);
  if (s.startsWith("rgb")) return _parseRgb(s);
  return null;
}

// ---------- relative luminance --------------------------------------------

function _sRgbToLinear(c) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance in [0, 1]. */
export function luminance({ r, g, b }) {
  return 0.2126 * _sRgbToLinear(r)
       + 0.7152 * _sRgbToLinear(g)
       + 0.0722 * _sRgbToLinear(b);
}

/**
 * Composite a semi-transparent foreground over an opaque background
 * before measuring luminance — because that's what the user actually
 * sees. `fg.a` is opacity in [0, 1].
 */
function _composite(fg, bg) {
  if (fg.a === undefined || fg.a >= 1) return fg;
  const a = _clamp01(fg.a);
  return {
    r: fg.r * a + bg.r * (1 - a),
    g: fg.g * a + bg.g * (1 - a),
    b: fg.b * a + bg.b * (1 - a),
    a: 1,
  };
}

/**
 * WCAG contrast ratio between two colours. Foreground can be semi-
 * transparent — it's composited over the background first.
 * Returns a number in [1, 21], rounded to two decimals.
 */
export function contrastRatio(fgStr, bgStr) {
  const fg = parseColor(fgStr);
  const bg = parseColor(bgStr);
  if (!fg || !bg) return null;
  const bgOpaque = { ...bg, a: 1 };
  const fgComposited = _composite(fg, bgOpaque);
  const L1 = luminance(fgComposited);
  const L2 = luminance(bgOpaque);
  const ratio = (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
  return Math.round(ratio * 100) / 100;
}

/** Human-friendly WCAG rating.
 *  Body text at 14pt regular (or 18pt bold+) needs 4.5+ for AA, 7+ for AAA.
 *  Large text (18pt regular or 14pt bold) needs 3+ for AA, 4.5+ for AAA.
 */
export function ratingFor(ratio) {
  if (ratio == null) return { label: "n/a", ok: false, tone: "muted" };
  if (ratio >= 7) return { label: "AAA", ok: true, tone: "excellent" };
  if (ratio >= 4.5) return { label: "AA", ok: true, tone: "good" };
  if (ratio >= 3) return { label: "AA large", ok: false, tone: "warn" };
  return { label: "Fail", ok: false, tone: "bad" };
}

/**
 * Read the effective CSS variable value from the closest `.ah-root`.
 * Falls back to `document.documentElement` if there is none. Returns
 * a string like "#0f172a" or "rgb(15, 23, 42)".
 */
export function readAhVar(varName, rootEl = null) {
  const el = rootEl || document.querySelector(".ah-root") || document.documentElement;
  if (!el) return null;
  const val = getComputedStyle(el).getPropertyValue(varName);
  return val ? val.trim() : null;
}

/**
 * One-shot audit: reads --ah-fg / --ah-bg / --ah-block-fg / --ah-block-bg
 * off the current theme and returns each contrast pair with rating.
 */
export function auditThemeContrast(rootEl = null) {
  const bg = readAhVar("--ah-bg", rootEl);
  const fg = readAhVar("--ah-fg", rootEl);
  const blockBg = readAhVar("--ah-block-bg", rootEl);
  const blockFg = readAhVar("--ah-block-fg", rootEl);

  const pageRatio = contrastRatio(fg, bg);
  const blockRatio = contrastRatio(blockFg, blockBg);

  return {
    page: {
      fg,
      bg,
      ratio: pageRatio,
      rating: ratingFor(pageRatio),
    },
    block: {
      fg: blockFg,
      bg: blockBg,
      ratio: blockRatio,
      rating: ratingFor(blockRatio),
    },
  };
}


// ---------- auto-adjustment for Links theme -------------------------------

function _rgbToHex({ r, g, b }) {
  const to = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Pick black or white — whichever has higher contrast against `bgStr`.
 *  Falls back to `#111111` when parsing fails. */
export function bestTextOn(bgStr) {
  const bg = parseColor(bgStr);
  if (!bg) return "#111111";
  const withWhite = contrastRatio("#ffffff", bgStr);
  const withBlack = contrastRatio("#111111", bgStr);
  return withWhite > withBlack ? "#ffffff" : "#111111";
}

/**
 * v146.53 — Auto-adjust the Links-theme palette so every text surface
 * stays readable no matter which three colours the creator picked.
 *
 * The creator picks three colours: `bg`, `pill`, `fg`. Their `fg` is
 * their creative choice for PAGE text (the main headline colour on the
 * page background). We keep it. Two other surfaces need care:
 *
 *   • Text INSIDE a pill button. If `fg` doesn't clear WCAG AA against
 *     `pill`, we swap it for whichever of black/white passes best.
 *   • Muted / sub text. We derive a fade of the chosen text colour on
 *     each surface so hints, footers, and secondary copy never render
 *     as hard-coded dark grey on a dark background.
 *
 * Returns a `{ blockFg, muted, blockMuted, pageBestFg }` bundle that
 * the caller drops into the ah-root inline style block.
 */
export function deriveLinksVars({ bg, pill, fg }) {
  const bgStr = bg || "#4a5568";
  const pillStr = pill || "#ffffff";
  const fgStr = fg || "#1a1a1a";

  // Text inside the pill needs ≥ AA (4.5:1) against the pill colour.
  const fgOnPill = contrastRatio(fgStr, pillStr);
  const blockFg = fgOnPill != null && fgOnPill >= 4.5
    ? fgStr
    : bestTextOn(pillStr);

  // Secondary text uses 65% of the primary text colour blended
  // atop each surface. We ship a semi-transparent variant so it
  // gracefully composits over any bg-image the creator may add.
  const mkAlpha = (hex, a) => {
    const c = parseColor(hex);
    if (!c) return `rgba(0, 0, 0, ${a})`;
    return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${a})`;
  };

  // If page fg is bad against page bg, fall back to a luminance-based
  // pick — but only for MUTED sub-text. Primary heading stays user's
  // pick so their creative intent survives.
  const pageBestFg = contrastRatio(fgStr, bgStr) >= 4.5 ? fgStr : bestTextOn(bgStr);

  return {
    blockFg,                            // text INSIDE a pill button
    muted: mkAlpha(pageBestFg, 0.72),   // secondary text on page bg
    blockMuted: mkAlpha(blockFg, 0.72), // secondary text inside pills
    pageBestFg,                         // safe fallback for stubborn corners
  };
}
